import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import ChildSwitcher from "../components/ChildSwitcher";
import { Send, Lock, Shield, CheckCheck, Crown } from "lucide-react";
import { encryptForRecipients, decryptForMe } from "../lib/e2e";
import { fetchPublicKey, getLocalPrivateJwk } from "../lib/keys";

function formatTime(iso) {
  return new Date(iso).toLocaleString([], {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function Messages() {
  const { user } = useAuth();
  const { selected } = useChild();
  const [messages, setMessages] = useState([]);
  const [decrypted, setDecrypted] = useState({}); // {message_id: plaintext}
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  // Load messages on selected change
  useEffect(() => {
    if (!selected) {
      setMessages([]);
      setDecrypted({});
      return;
    }
    api.get(`/children/${selected.id}/messages`).then((r) => setMessages(r.data));
  }, [selected]);

  // Decrypt encrypted messages whenever they change
  useEffect(() => {
    if (!user) return;
    const priv = getLocalPrivateJwk(user.id);
    if (!priv) return;
    (async () => {
      const updates = {};
      for (const m of messages) {
        if (m.is_legacy || decrypted[m.id]) continue;
        const wrappedB64 = m.aes_for && m.aes_for[user.id];
        const ctB64 = m.ciphertexts && m.ciphertexts[user.id];
        if (!wrappedB64 || !ctB64 || !m.iv) {
          updates[m.id] = "(this letter was sealed for another reader)";
          continue;        }
        try {
          const plain = await decryptForMe({
            wrappedAesB64: wrappedB64,
            ivB64: m.iv,
            ciphertextB64: ctB64,
            privateJwk: priv,
          });
          updates[m.id] = plain;
        } catch (_e) {
          updates[m.id] = "(this letter was sealed before this device existed)";
        }
      }
      if (Object.keys(updates).length) {
        setDecrypted((d) => ({ ...d, ...updates }));
      }
    })();
  }, [messages, user, decrypted]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, decrypted]);

  // Mark letters from OTHERS as read once they appear in the user's thread.
  useEffect(() => {
    if (!user) return;
    const unread = messages.filter(
      (m) => m.from_user_id !== user.id && !(m.read_by || {})[user.id]
    );
    if (unread.length === 0) return;
    const id = setTimeout(() => {
      Promise.all(
        unread.map((m) =>
          api
            .post(`/messages/${m.id}/read`)
            .then((r) => r.data)
            .catch(() => null)
        )
      ).then((rows) => {
        if (!rows.length) return;
        setMessages((prev) =>
          prev.map((m) => {
            const upd = rows.find((r) => r && r.id === m.id);
            return upd ? { ...m, read_by: upd.read_by } : m;
          })
        );
      });
    }, 900);
    return () => clearTimeout(id);
  }, [messages, user]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selected || !user) return;
    setBusy(true);
    try {
      // Determine recipients: caregiver + all parents of this child (including self)
      const recipientIds = new Set(selected.parent_ids || []);
      if (selected.caregiver_id) recipientIds.add(selected.caregiver_id);
      recipientIds.add(user.id);
      // Fetch all public keys
      const pubKeys = {};
      for (const rid of recipientIds) {
        pubKeys[rid] = await fetchPublicKey(rid);
      }
      const haveKeys = Object.values(pubKeys).filter(Boolean).length;
      if (haveKeys < 2) {
        // Recipient hasn't set up encryption yet — fall back to legacy plaintext send.
        const r = await api.post("/messages", {
          child_id: selected.id,
          text: text.trim(),
        });
        setMessages([...messages, r.data]);
        setDecrypted((d) => ({ ...d, [r.data.id]: r.data.text }));
        setText("");
        return;
      }

      const { iv, ciphertext, aes_for } = await encryptForRecipients(text.trim(), pubKeys);
      const r = await api.post("/messages/encrypted", {
        child_id: selected.id,
        iv,
        ciphertext,
        aes_for,
      });
      setMessages([...messages, r.data]);
      setDecrypted((d) => ({ ...d, [r.data.id]: text.trim() }));
      setText("");
    } finally {
      setBusy(false);
    }
  };

  const renderText = (m) => {
    if (m.is_legacy) return m.text;
    return decrypted[m.id] || "…";
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-4">
          <ChildSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-6 pb-12 fade-up">
        <span className="eyebrow">Letters</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
          Between you and{" "}
          <em className="italic text-[#8A9984]">
            {user?.role === "parent" ? "the caregiver" : "the parent"}.
          </em>
        </h1>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <span className="trust-pill" data-testid="messages-trust">
            <Lock size={11} strokeWidth={1.5} />
            End-to-end encrypted on this device
          </span>
          <span className="text-xs text-[#6A6863] tracking-wide">
            A single, sacred thread.
          </span>
        </div>

        <section className="mt-12 space-y-12">
          {messages.length === 0 && (
            <p
              data-testid="messages-empty"
              className="font-serif text-xl italic text-[#6B7280]"
            >
              No messages yet. Your child's teacher will reach out when there's
              something to share.
            </p>
          )}
          {messages.map((m, i) => {
            const mine = m.from_user_id === user?.id;
            const others = Object.entries(m.read_by || {}).filter(
              ([uid]) => uid !== m.from_user_id
            );
            const readByOthers = others.length;
            return (
              <article
                key={m.id}
                data-testid={`message-${i}`}
                className={`${
                  mine ? "pl-0 border-l-0" : "pl-5 sm:pl-8 border-l-2 border-[#9BA995]"
                } fade-up`}
              >
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="eyebrow">{m.from_user_name}</span>
                  {m.is_director && (
                    <span
                      data-testid={`message-${i}-director-badge`}
                      className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#1A1B2E] bg-[#FFB800] rounded-full px-2.5 py-0.5"
                    >
                      <Crown size={10} strokeWidth={1.5} />
                      Director
                    </span>
                  )}
                  <span className="text-xs text-[#6A6863]">·</span>
                  <span className="text-xs text-[#6A6863] tracking-wide">
                    {formatTime(m.created_at)}
                  </span>
                  {!m.is_legacy && (
                    <span
                      data-testid={`message-${i}-encrypted-badge`}
                      className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#8A9984]"
                    >
                      <Shield size={10} strokeWidth={1.5} />
                      encrypted
                    </span>
                  )}
                  {m.is_legacy && (
                    <span className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase text-[#6A6863]/70">
                      (before encryption)
                    </span>
                  )}
                </div>
                <p
                  data-testid={`message-${i}-text`}
                  className="font-serif text-xl sm:text-2xl leading-snug text-[#2C2B29]"
                >
                  {renderText(m)}
                </p>
                {mine && (
                  <div
                    data-testid={`message-${i}-receipt`}
                    className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#8A9984]"
                  >
                    <CheckCheck
                      size={11}
                      strokeWidth={1.5}
                      style={{ opacity: readByOthers ? 1 : 0.35 }}
                    />
                    {readByOthers ? "Read" : "Sent"}
                  </div>
                )}
              </article>
            );
          })}
          <div ref={endRef} />
        </section>

        <form
          onSubmit={send}
          className="mt-16 rounded-3xl border hairline bg-white p-6 sm:p-8"
        >
          <label className="eyebrow">A letter, in your own time</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="Write softly…"
            data-testid="messages-input"
            className="orbit-input mt-4 resize-none"
          />
          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <span className="text-xs text-[#6A6863] tracking-wide inline-flex items-center gap-2">
              <Shield size={11} strokeWidth={1.5} className="text-[#8A9984]" />
              Encrypted before it leaves your device.
            </span>
            <button
              type="submit"
              disabled={busy || !text.trim()}
              data-testid="messages-send"
              className="btn-primary"
            >
              {busy ? "Sealing…" : "Send"}
              <Send size={14} strokeWidth={1.5} />
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
